import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  DateField,
  Field,
  Filters,
  Page,
  PageHeader,
  Pagination,
  Select,
  StatCard,
  StatCardList,
  Table,
  type Action,
} from '@evogard/ui';
import { count, businessDate, formatDate, loadFailureMessage } from '@evogard/domain';
import { ENTRY_TYPES } from '@evogard/contract';
import type { CashEntry, EntryType } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import { MoneyValue, currentPeriod, firstDayOfPeriod } from './common.js';

/** Como se escreve cada direção. Fechado no contrato, traduzido uma vez aqui. */
// @valor
const LABEL_TYPE: Record<EntryType, string> = {
  entrada: 'Entrada',
  saida: 'Saída',
};

/**
 * A linha de apoio dos três números: o período que eles somam.
 *
 * Ela repete em cada cartão de propósito. Os três moram numa grade sem caixa em
 * volta — não há cabeçalho comum para pendurar a frase —, e "do período
 * inteiro, e não da página abaixo" é exatamente o que nenhum deles pode deixar
 * de dizer: a alternativa é alguém somar a página e obter outro número, e as
 * duas somas pareceriam igualmente certas.
 */
function periodSupport(cashbook: { de: string; until: string } | undefined): string {
  return cashbook
    ? `De ${formatDate(cashbook.de)} a ${formatDate(cashbook.until)}, inclusive — e não a página abaixo.`
    : 'Do período inteiro, e não da página abaixo.';
}

/**
 * O livro-caixa do comercial — o que entrou, o que saiu e o saldo do período.
 *
 * ## O sinal mora no TIPO, e o valor é sempre positivo
 *
 * Não há campo de sinal, e digitar `-100` numa saída é recusado pelo banco. Com
 * o sinal em duas colunas, uma saída de −R$ 100,00 **somaria** ao caixa — e a
 * conta fecharia errado sem nenhum erro aparecer. Esta tela não repete a
 * checagem: ela mostra a recusa do servidor.
 *
 * ## Lançamento não se apaga. O estorno é um lançamento novo
 *
 * O sistema antigo apagava o registro e perdia o histórico. Aqui existe
 * arquivar — para a linha que nunca deveria ter existido —, e desarquivar,
 * porque arquivar errado não pode ser irreversível. Corrigir um valor é lançar o
 * contrário, não reescrever o original.
 *
 * ## `data` é a do MOVIMENTO, e não a do vencimento
 *
 * A linha existe porque o dinheiro se moveu. É a escolha que torna o total de um
 * período uma pergunta com uma resposta só — o sistema antigo guarda duas datas
 * porque a mesma tabela faz o livro-caixa e o contas a pagar, e por isso o
 * "total do mês" de lá depende de qual das duas quem consultou escolheu.
 *
 * ## Categoria é sugestão, e não catálogo
 *
 * A lista de categorias não está em repositório nenhum: quem a digitou foi a
 * operação, em produção. Enquanto ela não for extraída, esta tela oferece as
 * categorias **já usadas nesta base** em vez de um campo vazio — e nada impede
 * uma nova. O custo está escrito no formulário: "Combustível" e "combustivel"
 * viram duas categorias no relatório, e ninguém vê erro.
 *
 * ## Os totais são do PERÍODO INTEIRO, nunca da página
 *
 * Está dito na tela porque a alternativa é alguém somar a página e obter outro
 * número — e as duas somas pareceriam igualmente certas.
 */
export function CashBookScreen() {
  const { data: context } = useSessionContext();
  const canPost = can(context, 'sales.finance', 'lancar');

  const today = businessDate(new Date());
  const startOfMonth = firstDayOfPeriod(currentPeriod());
  const [from, setFrom] = useState(startOfMonth);
  const [until, setUntil] = useState(today);
  const [tipo, setType] = useState<EntryType | ''>('');
  const [categoria, setCategory] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [posting, setPosting] = useState(false);
  /*
   * O painel de ações fecha ANTES de a ação rodar, então a recusa de arquivar
   * aparece acima da tabela e não na linha. Uma mutação que vivesse no botão
   * desmontaria junto com o painel, levando o erro embora antes de a resposta
   * chegar — é a mesma subida que `claim` fez na mesa de demandas.
   */
  const [actionError, setActionError] = useState<string | null>(null);

  /*
   * Até 15/09 a lista vinha com `page: 1, perPage: 50` e o aviso de corte
   * embaixo do título. O aviso existia porque não havia como virar página.
   */
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const tenantId = context?.currentTenant.id;
  const client = useQueryClient();

  /**
   * Arquivar e desarquivar, no mesmo ato.
   *
   * Um ato só porque a rota é uma só e o campo é obrigatório: um `arquivado`
   * opcional faria "arquive" e "não quis mexer" serem a mesma requisição.
   *
   * Não há confirmação, e é deliberado: arquivar aqui é reversível pelo próprio
   * painel, e um diálogo em cima de um ato reversível ensina a clicar "sim" sem
   * ler — o que estraga a confirmação onde ela importa de verdade.
   */
  const archive = useMutation({
    mutationFn: async (entry: CashEntry) => {
      const r = await api.sales.archiveEntry({
        params: { id: entry.id },
        body: { archived: entry.archivedAt === null },
      });
      return ok(r, 200, 'Não foi possível arquivar.');
    },
    onMutate: () => setActionError(null),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['comercial', 'caixa'] }),
    onError: (e: Error) => setActionError(e.message),
  });

  const cashbookQuery = useQuery({
    queryKey: [
      'comercial',
      'caixa',
      tenantId,
      from,
      until,
      tipo,
      categoria,
      showArchived,
      page,
      perPage,
    ],
    queryFn: async () => {
      const r = await api.sales.livroCashbook({
        query: {
          page,
          perPage,
          de: from,
          until,
          tipo: tipo || undefined,
          categoria: categoria || undefined,
          includeArchived: showArchived ? 'true' : undefined,
        },
      });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'o livro-caixa do período'));
      }
      return r.body;
    },
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });

  const cashbook = cashbookQuery.data;
  const total = cashbook?.total;

  /*
   * Estreitar o recorte encurta a lista, e a página em que se estava deixa de
   * existir: mudar o período estando na página 4 pediria um `offset` além do
   * fim, e a resposta seria uma tabela vazia sob um cartão dizendo o total.
   */
  useEffect(() => {
    setPage(1);
  }, [tenantId, from, until, tipo, categoria, showArchived, perPage]);

  return (
    <Page measure="cheia">
      <PageHeader
        title="Livro-caixa"
        actions={canPost && <Button onClick={() => setPosting(true)}>Lançar</Button>}
      >
        O que entrou e o que saiu em{' '}
        <strong className="font-medium text-texto">{context?.currentTenant.nome}</strong>.
        A data de cada linha é a do movimento — não a de um vencimento.
      </PageHeader>

      {posting && canPost && (
        <EntryForm
          categories={cashbook?.categories ?? []}
          toClose={() => setPosting(false)}
        />
      )}

      {cashbookQuery.isError && <Alert tone="erro">{cashbookQuery.error.message}</Alert>}

      {actionError && <Alert tone="erro">{actionError}</Alert>}

      {/*
       * Os totais como KPI da casa, e não mais um `Card` com três `<dt>`/`<dd>`
       * escritos aqui.
       *
       * O `Total` privado desta tela desenhava `text-xl font-bold` sobre um
       * `rotulo` — que é, letra por letra, o que `Metric` faz — e o esqueleto
       * dele era `h-7 w-24`, que é o de `Metric` menos a segunda barra. Era a
       * quinta cópia do mesmo número, e a que tinha superfície própria.
       *
       * O período sai do cabeçalho do cartão e vira a linha de apoio de cada
       * um: sem caixa em volta dos três não há onde pendurar um título comum, e
       * "do período inteiro, e não da página abaixo" é justamente o que nenhum
       * destes números pode deixar de dizer.
       */}
      <StatCardList>
        <StatCard
          icon="seta-baixo"
          label="Entradas"
          value={
            cashbook?.totals.entry === undefined ? undefined : (
              <MoneyValue value={cashbook.totals.entry} />
            )
          }
          support={periodSupport(cashbook)}
        />
        <StatCard
          icon="seta-cima"
          label="Saídas"
          value={
            cashbook?.totals.exit === undefined ? undefined : (
              <MoneyValue value={cashbook.totals.exit} />
            )
          }
          support={periodSupport(cashbook)}
        />
        <StatCard
          icon="dinheiro"
          label="Saldo"
          value={
            cashbook?.totals.balance === undefined ? undefined : (
              <MoneyValue value={cashbook.totals.balance} />
            )
          }
          /*
           * O saldo vem do servidor. Subtrair aqui exigiria fazer conta com o
           * texto do dinheiro, que é onde `19.99 * 100` vira
           * `1998.9999999999998` — e num livro-caixa esse centavo é o que faz a
           * conciliação do mês não fechar.
           */
          support="Entradas menos saídas, calculado no servidor; negativo é um resultado legítimo."
        />
      </StatCardList>

      {(!cashbookQuery.isError || cashbookQuery.data) && (
        <Card
          title={
            total === undefined
              ? 'Carregando…'
              : count(total, 'lançamento', 'lançamentos')
          }
          bleed
        >
          <Table
            paginate={false}
            /*
             * Sem campo de busca: a rota não recorta por texto. Um campo de
             * pesquisa que não pesquisa nada é a barra prometendo o que o
             * servidor não faz — e a descrição já é a coluna mais longa da
             * tabela, então ele pareceria buscar nela.
             *
             * **O período não tem vazio**, e o "Limpar" do painel aplica o
             * vazio: os dois extremos caem para o padrão desta tela — do dia 1º
             * da competência corrente até hoje. Cada um diz isso no `help`,
             * porque um período aberto é o que a rota recusa.
             */
            toolbar={
              <Filters
                values={{
                  from,
                  until,
                  tipo,
                  categoria,
                  archived: showArchived ? 'sim' : '',
                }}
                onApply={(values) => {
                  setFrom(String(values.from ?? '') || startOfMonth);
                  setUntil(String(values.until ?? '') || today);
                  setType(String(values.tipo ?? '') as EntryType | '');
                  setCategory(String(values.categoria ?? ''));
                  setShowArchived(values.archived === 'sim');
                }}
                fields={[
                  {
                    type: 'intervalo',
                    label: 'Movimento em',
                    from: 'from',
                    to: 'until',
                    help: 'Inclusive nos dois extremos. Vazio volta ao mês corrente até hoje.',
                  },
                  {
                    type: 'selecao',
                    name: 'tipo',
                    label: 'Direção',
                    placeholder: 'Entradas e saídas',
                    options: ENTRY_TYPES.map((t) => ({ value: t, label: LABEL_TYPE[t] })),
                  },
                  {
                    /*
                     * As categorias vêm da resposta — são as que esta base já
                     * usou. Não é catálogo: não há tabela, não há "ativo", e
                     * nada impede uma nova.
                     */
                    type: 'selecao',
                    name: 'categoria',
                    label: 'Categoria',
                    placeholder: 'Todas',
                    options: (cashbook?.categories ?? []).map((c) => ({
                      value: c,
                      label: c,
                    })),
                  },
                  {
                    /*
                     * `selecao` de duas opções, e não caixa de marcar: dentro de
                     * um painel a caixa não diz qual é o outro estado dela. Ver
                     * o cabeçalho de `FilterField`.
                     */
                    type: 'selecao',
                    name: 'archived',
                    label: 'Arquivados',
                    placeholder: 'Só os vigentes',
                    options: [{ value: 'sim', label: 'Vigentes e arquivados' }],
                  },
                ]}
              />
            }
            label="Lançamentos do livro-caixa"
            items={cashbook?.items ?? []}
            loading={cashbookQuery.isLoading}
            rowKey={(l) => String(l.id)}
            empty={
              tipo || categoria
                ? 'Nenhum lançamento com esses filtros neste período.'
                : 'Nenhum lançamento neste período. Isso é sobre as datas escolhidas — a base pode ter lançamentos fora delas.'
            }
            rowTitle={(l) => l.descricao}
            rowSubtitle={(l) => `${formatDate(l.data)} · ${l.categoria}`}
            rowIcon="livro"
            /*
             * A ação da linha no painel das reticências, e não mais um botão
             * "Arquivar" numa coluna chamada "Ação".
             *
             * O rótulo continua dizendo o que o clique faz, e não o estado da
             * linha: "Desarquivar" na arquivada, "Arquivar" na vigente. Sem
             * `sales.finance:lancar` a lista é vazia, e a linha fica sem
             * gatilho — que é a resposta certa, e não um item cinza anunciando
             * uma capacidade que aquela pessoa não tem.
             */
            rowActions={(l): Action[] =>
              canPost
                ? [
                    {
                      label: l.archivedAt ? 'Desarquivar' : 'Arquivar',
                      icon: l.archivedAt ? 'atualizar' : 'bloqueado',
                      run: () => archive.mutate(l),
                    },
                  ]
                : []
            }
            columns={[
              {
                title: 'Data',
                minWidth: '7rem',
                cell: (l) => <span className="tabular-nums">{formatDate(l.data)}</span>,
              },
              {
                title: 'Descrição',
                alignment: 'inicio' as const,
                minWidth: '16rem',
                cell: (l) => (
                  <div className="min-w-0">
                    <p
                      className={l.archivedAt ? 'truncate text-texto-suave' : 'truncate'}
                    >
                      {l.descricao}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-texto-suave">
                      {l.categoria}
                      {/* nulo é o lançamento que veio de importação: a autoria
                          está na trilha de auditoria, não nesta linha */}
                      {l.createdBy ? <> · {l.createdBy.nome}</> : <> · importado</>}
                    </p>
                  </div>
                ),
              },
              {
                title: 'Direção',
                cell: (l) => <EntryDirection entry={l} />,
              },
              {
                title: 'Valor',
                alignment: 'fim',
                cell: (l) => <MoneyValue value={l.value} />,
              },
            ]}
          />

          <Pagination
            page={page}
            perPage={perPage}
            total={total ?? 0}
            onChange={setPage}
            onPerPageChange={setPerPage}
            label="lançamentos"
            /* o cartão é `bleed`; o espaçamento é de quem entra nele */
            className="border-t border-borda-suave px-5 py-4"
          />
        </Card>
      )}
    </Page>
  );
}

/**
 * A direção de um lançamento.
 *
 * Selo com cor, ícone e texto — os três, e nunca só a cor: quem não distingue
 * vermelho de verde precisa do símbolo, e quem usa leitor de tela precisa do
 * texto. A entrada **não** é `sucesso`: verde é estado, e entrar dinheiro não é
 * um estado bom nem ruim, é uma direção. Um verde aqui pintaria de acerto o
 * lançamento que alguém digitou errado.
 */
function EntryDirection({ entry }: { entry: CashEntry }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge tone={entry.tipo === 'entrada' ? 'informacao' : 'neutro'}>
        {LABEL_TYPE[entry.tipo]}
      </Badge>
      {entry.archivedAt && <Badge tone="alerta">Arquivado</Badge>}
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * Lançar
 * ------------------------------------------------------------------------- */

/**
 * Lançar uma entrada ou uma saída.
 *
 * O valor é sempre positivo e a direção é o tipo. Quem recusa um valor negativo
 * ou zero é o `check` do banco, e a mensagem que aparece é a dele — esta tela
 * não reimplementa a regra, porque duas verdades divergem e a do banco é a que
 * vale.
 *
 * A categoria é um campo de texto com sugestões, e não um seletor fechado: não
 * há catálogo, e um seletor fechado impediria de registrar o que a operação já
 * usa. O aviso diz o preço disso.
 */
function EntryForm({
  categories,
  toClose,
}: {
  categories: readonly string[];
  toClose: () => void;
}) {
  const client = useQueryClient();
  const [tipo, setType] = useState<EntryType>('entrada');
  const [categoria, setCategory] = useState('');
  const [value, setValue] = useState('');
  const [data, setData] = useState(businessDate(new Date()));
  const [descricao, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const post = useMutation({
    mutationFn: async () => {
      const r = await api.sales.post({
        body: {
          tipo,
          categoria: categoria.trim(),
          /* o texto que a pessoa digitou, na forma da coluna `numeric(12,2)`:
             nada de `Number(...)` no caminho do dinheiro */
          value: value.trim(),
          data,
          descricao: descricao.trim(),
        },
      });
      return ok(r, 201, 'Não foi possível lançar.');
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['comercial', 'caixa'] });
      toClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Card
      title="Lançar no caixa"
      descricao="O valor é sempre positivo — quem diz se soma ou subtrai é a direção."
      actions={
        <Button variant="discreto" size="sm" onClick={toClose}>
          Cancelar
        </Button>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          post.mutate();
        }}
      >
        {error && <Alert tone="erro">{error}</Alert>}

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Direção"
            value={tipo}
            onChange={(e) => setType(e.target.value as EntryType)}
          >
            {ENTRY_TYPES.map((t) => (
              <option key={t} value={t}>
                {LABEL_TYPE[t]}
              </option>
            ))}
          </Select>

          <Field
            label="Valor"
            required
            inputMode="decimal"
            placeholder="0.00"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            help="Positivo, com ponto: 1234.56. Uma saída não se digita com sinal."
          />

          <div>
            <Field
              label="Categoria"
              required
              maxLength={80}
              list="categorias-do-caixa"
              value={categoria}
              onChange={(e) => setCategory(e.target.value)}
              help="As sugestões são as categorias já usadas nesta base. “Combustível” e “combustivel” viram duas no relatório."
            />
            {/*
             * `datalist` e não `select`: é sugestão, não catálogo. Um seletor
             * fechado impediria de registrar uma categoria que a operação usa e
             * que a plataforma ainda não viu — e a lista de verdade só existe no
             * banco de produção do sistema antigo.
             */}
            <datalist id="categorias-do-caixa">
              {categories.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </div>

          <DateField
            label="Data do movimento"
            required
            value={data}
            onChange={setData}
            help="Quando o dinheiro se moveu — não um vencimento."
          />

          <div className="sm:col-span-2">
            <Field
              label="Descrição"
              required
              maxLength={400}
              value={descricao}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>

        <div>
          <Button type="submit" loading={post.isPending}>
            Lançar
          </Button>
        </div>
      </form>
    </Card>
  );
}
